/**
 * Ticket ME1: Mentorings – gemeinsame Typen und reine Logik.
 *
 * Bewusst ohne React und ohne Datenbankzugriff, damit Anfrageziel, Preispflicht,
 * Reihenfolge und Abschnittslisten am Verhalten geprüft werden können.
 *
 * Mentorings werden angefragt, nicht gekauft. Deshalb gibt es hier keinen Preis
 * in Cent, keine Zahlungskennung und keine Kaufbarkeit – nur einen Klartextpreis
 * und ein Anfrageziel.
 */

import { z } from "zod";
import type { PublicPerson } from "@/lib/team-people";

/** Gemeinsames Präfix aller Mentoring-Adressen. */
export const MENTORING_PREFIX = "/wissen/mentorings";

/**
 * Ticket ME2: Kennungen der Abschnitte, auf die von oben gesprungen wird.
 * Genau eine Stelle, damit Knopf und Abschnitt nicht auseinanderlaufen.
 */
export const MENTORING_ANCHORS = {
  nextStep: "naechster-schritt",
  beschreibung: "beschreibung",
  problem: "das-problem",
  danach: "was-sie-danach-koennen",
  inhalte: "inhalte",
  faq: "faq",
  /** Buchungskarte mit dem Preis – Sprungziel nur auf kleinen Schirmen. */
  preis: "preis",
} as const;

export type MentoringStatus = "entwurf" | "veroeffentlicht";

export const MENTORING_STATUS_LABELS: Record<MentoringStatus, string> = {
  entwurf: "Entwurf",
  veroeffentlicht: "veröffentlicht",
};

/**
 * Ticket ME5: Wie ein Mentoring stattfindet. Genau ein Wert je Mentoring; die
 * Liste ist bewusst geschlossen, weil die Datenbank dieselben drei Werte
 * durchsetzt. Auf der Seite erscheint die Angabe im Kopfbereich neben Umfang
 * und Preis – gestaltet als eigene Angabe, nicht als Zielgruppen-Pille, damit
 * niemand „vor Ort" als Zielgruppe liest.
 */
export const MENTORING_DELIVERY_MODES = ["online", "hybrid", "vor_ort"] as const;
export type MentoringDeliveryMode = (typeof MENTORING_DELIVERY_MODES)[number];

export const MENTORING_DELIVERY_MODE_LABELS: Record<MentoringDeliveryMode, string> = {
  online: "online",
  hybrid: "hybrid",
  vor_ort: "vor Ort",
};

export const mentoringDeliveryModeSchema = z.enum(MENTORING_DELIVERY_MODES);

/** Unbekannte oder fehlende Werte gelten als „online" – nie ein Fehler. */
export function parseMentoringDeliveryMode(value: unknown): MentoringDeliveryMode {
  const parsed = mentoringDeliveryModeSchema.safeParse(value);
  return parsed.success ? parsed.data : "online";
}

/** Beschriftung der Durchführungsform, wie sie auf der Seite erscheint. */
export function mentoringDeliveryModeLabel(value: unknown): string {
  return MENTORING_DELIVERY_MODE_LABELS[parseMentoringDeliveryMode(value)];
}

/**
 * Ticket ME2: Eine Zielgruppe. Die Liste ist im Backend pflegbar; auf den
 * Seiten erscheinen die Bezeichnungen als kleine Pillen.
 */
export type MentoringAudience = {
  id: string;
  label: string;
  slug: string;
  sortOrder: number;
};

/** Reihenfolge der Pillen: gepflegte Zahl führt, bei Gleichstand die Bezeichnung. */
export function compareAudiences(a: MentoringAudience, b: MentoringAudience): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.label.localeCompare(b.label, "de");
}

/**
 * Kürzel einer neu angelegten Zielgruppe. Aus der Bezeichnung abgeleitet, damit
 * niemand zwei Felder füllen muss: Umlaute werden ausgeschrieben, alles Übrige
 * auf Kleinbuchstaben, Zahlen und Bindestriche zurückgeführt.
 */
export function audienceSlugFromLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}


/**
 * Ticket ME4: Warnung vor dem Löschen einer Zielgruppe. Sie verschwindet dann
 * bei allen Mentorings, denen sie zugeordnet war; die Mentorings selbst bleiben
 * unberührt. Die Zahl steht bewusst im Text, damit niemand eine Zielgruppe
 * entfernt, die an sechs Seiten hängt.
 */
export function audienceDeleteWarning(count: number): string {
  const used =
    count === 0
      ? "Diese Zielgruppe ist derzeit keinem Mentoring zugeordnet."
      : count === 1
        ? "Diese Zielgruppe ist 1 Mentoring zugeordnet."
        : `Diese Zielgruppe ist ${count} Mentorings zugeordnet.`;
  return `${used} Beim Löschen verschwindet sie dort überall. Die Mentorings selbst bleiben unverändert.`;
}

export const mentoringSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "Das Kürzel braucht mindestens drei Zeichen.")
  .max(80, "Das Kürzel ist zu lang.")
  .regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    "Erlaubt sind Kleinbuchstaben, Zahlen und einzelne Bindestriche.",
  );

/**
 * Ticket ME4: Alle Mentoring-Preise sind Bruttopreise. Der Hinweis steht an
 * genau einer Stelle und erscheint überall dort, wo ein Preis erscheint
 * (Kopfbereich und Karte in der Übersicht).
 */
export const MENTORING_PRICE_NOTE = "inkl. MwSt.";

/**
 * Räumt einen gepflegten Preistext auf: ein alter Nettozusatz („zzgl. MwSt.",
 * „zuzüglich Umsatzsteuer", „netto") wird entfernt, damit neben dem
 * Bruttohinweis nicht das Gegenteil steht. Die Zahl selbst bleibt unberührt.
 */
export function mentoringPriceLabel(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .replace(
      /[,;]?\s*(zzgl\.?|zuzügl\.?|zuzüglich)\s*(der\s+)?(gesetzl\.?\s*|gesetzlichen\s+)?(mwst\.?|ust\.?|umsatzsteuer|steuer)\.?$/i,
      "",
    )
    .replace(/[,;]?\s*\(?netto\)?\.?$/i, "")
    .trim();
}

/**
 * Ein Eintrag der Inhaltsliste (früher: ein Termin des Ablaufs).
 *
 * Ticket ME3: Je Eintrag gehört ein Bild dazu, im Backend pflegbar. Gepflegt
 * wird der Ablageort (`imagePath`); ausgeliefert wird eine kurz signierte
 * Adresse. Fehlt das Bild, entfällt die Bildstelle ersatzlos – kein
 * Platzhalterbild, keine Ersatzfläche.
 *
 * Ticket ME6: `highlight` setzt einen Eintrag von den Terminen ab (etwa das
 * Mentoringmaterial). Die Dauer ist damit freiwillig; bei einem abgesetzten
 * Eintrag wird sie hier ausgeräumt, damit dort auch dann nichts steht, wenn
 * jemand im Editor etwas eingetragen hatte. Das Ausräumen sitzt im Schema, also
 * an einer Stelle für Pflegemaske und Leseschicht.
 */
/**
 * Ein freiwilliges Textfeld eines Ablauf- oder Frageneintrags.
 *
 * Datensätze, die außerhalb der Pflegemaske entstanden sind, tragen in solchen
 * Feldern manchmal `null` statt eines leeren Textes. `default("")` greift nur
 * bei fehlenden Feldern, nicht bei `null` – deshalb wird `null` hier vorab in
 * einen leeren Text übersetzt. Sonst bricht das Speichern mit „Expected string,
 * received null" ab, obwohl das Feld freiwillig ist.
 */
function optionalItemText(max: number) {
  return z
    .union([z.string(), z.null()])
    .optional()
    .transform((value) => value ?? "")
    .pipe(z.string().trim().max(max));
}

export const mentoringScheduleItemSchema = z
  .object({
    label: z.string().trim().min(1, "Bitte eine Bezeichnung angeben.").max(200),
    content: optionalItemText(2000),
    duration: optionalItemText(120),
    imagePath: optionalItemText(500),
    highlight: z
      .union([z.boolean(), z.null()])
      .optional()
      .transform((value) => value ?? false),
  })
  .transform((item) => (item.highlight ? { ...item, duration: "" } : item));
export type MentoringScheduleItem = z.infer<typeof mentoringScheduleItemSchema>;

/** Ein Eintrag, wie er auf der öffentlichen Seite erscheint. */
export type MentoringScheduleView = MentoringScheduleItem & {
  imageUrl: string | null;
};

/** Eine häufige Frage. */
export const mentoringFaqItemSchema = z.object({
  question: z.string().trim().min(1, "Bitte eine Frage angeben.").max(300),
  answer: optionalItemText(3000),
});
export type MentoringFaqItem = z.infer<typeof mentoringFaqItemSchema>;

/**
 * Öffentliche Anzeigedaten einer Mentoring-Karte.
 *
 * Ticket ME2: Die Karte trägt keine Betreuung mehr (kein Gesicht in der
 * Übersicht) und statt eines einzelnen Zielgruppenworts eine Liste von
 * Zielgruppen. `priceStrikethroughLabel` ist leer, wenn kein Streichpreis
 * gepflegt ist – dann erscheint nur der Preis.
 *
 * Ticket ME3: Der Untertitel ist ersatzlos entfallen, auch aus der Datenbank.
 * An seiner Stelle steht im Kopfbereich die Kurzbeschreibung.
 */
export type MentoringCard = {
  slug: string;
  title: string;
  shortDescription: string;
  audiences: MentoringAudience[];
  scopeLabel: string;
  priceLabel: string;
  priceStrikethroughLabel: string;
  sortOrder: number;
  previewImageUrl: string | null;
  /**
   * Ticket KI1: Dauerhaft gültige Adresse des Vorschaubilds für die
   * Teilen-Angaben; die Anzeige nutzt weiter die signierte Adresse.
   */
  shareImageUrl: string | null;
  /**
   * Längliches Bild für den Kopfbereich der Detailseite. Fehlt es, springt
   * dort das quadratische Vorschaubild ein.
   */
  headerImageUrl: string | null;
};

/** Öffentliche Anzeigedaten einer Mentoring-Detailseite. */
export type MentoringPage = MentoringCard & {
  /** Nur auf der Detailseite: die Personenkarte im Abschnitt „Ihre Betreuung". */
  mentor: PublicPerson | null;
  /**
   * Ticket ME7: Beschreibung zwischen Kopfbereich und den beiden Kästen.
   * Gewöhnlicher Fließtext über die volle Breite; leer heißt: der Bereich
   * entfällt ersatzlos.
   */
  descriptionMd: string;
  problemMd: string;
  outcomeMd: string;
  /**
   * Ticket ME5: Vorspann unter der Überschrift „Die Inhalte des Mentorings".
   * Ist er leer, entfällt er ersatzlos und die Liste rückt auf. Der frühere
   * Abschnitt „Was enthalten ist" ist entfallen; sein Text ist beim Umbau
   * unverändert in dieses Feld gewandert.
   */
  contentsIntroMd: string;
  /** Ticket ME5: Wie das Mentoring stattfindet – online, hybrid oder vor Ort. */
  deliveryMode: MentoringDeliveryMode;
  schedule: MentoringScheduleView[];
  faqs: MentoringFaqItem[];
  seoTitle: string;
  seoDescription: string;
};


/**
 * Reihenfolge der Übersicht: das gepflegte Feld führt, bei Gleichstand der
 * Titel. Ausdrücklich nicht alphabetisch – die Reihenfolge ist eine
 * redaktionelle Entscheidung.
 */
export function compareMentorings<T extends { sortOrder: number; title: string }>(
  a: T,
  b: T,
): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.title.localeCompare(b.title, "de");
}

/**
 * Bedingungen fürs Veröffentlichen. Dieselben zwei, die auch die Datenbank
 * durchsetzt – hier nur früher sichtbar, damit niemand erst am Fehler der
 * Datenbank hängen bleibt:
 *  1. eine zugeordnete, für die Website freigegebene Betreuung,
 *  2. ein Preis im Klartext (Auflage aus der Freigabe zu ME1).
 * Bilder sind freiwillig.
 */
export function mentoringPublishIssues(input: {
  mentorProfileId: string | null | undefined;
  mentorIsPublic?: boolean;
  priceLabel: string | null | undefined;
  previewImagePath?: string | null | undefined;
}): string[] {
  const issues: string[] = [];
  if (!input.mentorProfileId) {
    issues.push("Ohne zugeordnete Betreuung kann nicht veröffentlicht werden.");
  } else if (input.mentorIsPublic === false) {
    issues.push(
      "Die zugeordnete Person ist im Team nicht für die Website freigegeben.",
    );
  }
  if (!(input.priceLabel ?? "").trim()) {
    issues.push("Ohne Preis kann nicht veröffentlicht werden.");
  }
  /**
   * Bilder sind ausdrücklich freiwillig: Vorschau- und Headerbild haben
   * unterschiedliche Formate und werden nicht mehr fürs Veröffentlichen
   * verlangt.
   */
  return issues;
}


/** Herkunftsmerkmal aus der Adresse des Kontaktformulars, streng geprüft. */
export function parseMentoringOrigin(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const parsed = mentoringSlugSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Öffentliche Adresse eines Mentorings. */
export function mentoringPath(slug: string): string {
  return `${MENTORING_PREFIX}/${slug.trim().toLowerCase()}`;
}

/**
 * Eingabeprüfung der Pflegemaske. Sie deckt genau die Zusicherungen ab, die
 * auch die Datenbank durchsetzt (Anfrageziel, Preis beim Veröffentlichen),
 * damit die Meldung im Formular erscheint und nicht als Datenbankfehler.
 */
export const mentoringFormSchema = z
  .object({
    slug: mentoringSlugSchema,
    sortOrder: z.number().int().min(0).max(9999),
    status: z.enum(["entwurf", "veroeffentlicht"]),
    title: z.string().trim().min(1, "Bitte einen Titel angeben.").max(200),
    /**
     * Ticket ME3: Der Untertitel ist entfallen. Im Kopfbereich steht die
     * Kurzbeschreibung – ein Text weniger, der dasselbe zweimal sagt.
     */
    shortDescription: z.string().trim().max(600).default(""),
    /**
     * Ticket ME2: Das alte Einzelwort wird nicht mehr gepflegt; das Feld bleibt
     * nur, damit vorhandene Datensätze durch das Formular kommen. Gepflegt wird
     * über `audienceIds`.
     */
    audienceLabel: z.string().trim().max(40).default(""),
    audienceIds: z.array(z.string().uuid()).max(20).default([]),
    scopeLabel: z.string().trim().max(120).default(""),
    /** Ticket ME5: Durchführungsform, ein Wert je Mentoring. */
    deliveryMode: mentoringDeliveryModeSchema.default("online"),
    priceLabel: z.string().trim().max(120).default(""),
    priceStrikethroughLabel: z.string().trim().max(120).default(""),
    mentorProfileId: z.string().uuid().nullable(),
    /** Ticket ME7: Beschreibung über den beiden Kästen, freiwillig. */
    descriptionMd: z.string().max(20000).default(""),
    problemMd: z.string().max(20000).default(""),
    outcomeMd: z.string().max(20000).default(""),
    /**
     * Ticket ME5: Vorspann der Inhalte. Der frühere Abschnitt „Was enthalten
     * ist" ist entfallen; sein Text steht jetzt hier.
     */
    contentsIntroMd: z.string().max(20000).default(""),
    schedule: z.array(mentoringScheduleItemSchema).max(50),
    faqs: z.array(mentoringFaqItemSchema).max(50),
    previewImagePath: z.string().nullable(),
    /** Längliches Bild für den Kopfbereich der Detailseite, freiwillig. */
    headerImagePath: z.string().nullable().default(null),
    seoTitle: z.string().trim().max(200).default(""),
    seoDescription: z.string().trim().max(400).default(""),
  })
  .superRefine((values, ctx) => {
    if (values.status !== "veroeffentlicht") return;
    for (const issue of mentoringPublishIssues({
      mentorProfileId: values.mentorProfileId,
      priceLabel: values.priceLabel,
      previewImagePath: values.previewImagePath,
    })) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue });
    }
  });


export type MentoringFormValues = z.input<typeof mentoringFormSchema>;
